/**
 * Markups and takeoff measurements in format v1 (Phase 2, additive).
 *
 * Every payload mirrors Centerline for Mac's Swift `Codable` encoding field for
 * field, so the Mac app can decode a cloud markup's `style` with its own types
 * and vice versa:
 * - CGPoint → `[x, y]`; geometry is PDF page points in unrotated page space.
 * - `Data` (logos, signature images) → base64 string (JSONEncoder's default).
 * - `Date` → ISO-8601 string.
 * - Colours stay Centerline palette names (`AppearanceColor`), drawn with the
 *   macOS light-appearance values (see render/colors.ts).
 *
 * The one structural change from the Mac model: items point at a page by its
 * permanent `pageId` (not `pageIndex`), and a markup's box is a `Box`
 * `[x, y, width, height]` instead of CGRect's `[[x, y], [w, h]]`.
 */
import type { Box, Id } from "../types.js";

/** A point in PDF page space, `[x, y]` (Swift CGPoint's JSON form). */
export type Vec2 = [number, number];

export const APPEARANCE_COLORS = ["ink", "navy", "teal", "blue", "green", "yellow", "red", "orange", "purple", "gray", "white", "clear"] as const;
export type AppearanceColor = (typeof APPEARANCE_COLORS)[number];

export const APPEARANCE_FONTS = [
  "helvetica", "helveticaBold", "avenir", "menlo",
  "avenirHeavy", "futuraBold", "timesBold", "georgiaBold", "courierBold", "americanTypewriter", "impact",
  "times", "courier", "georgia", "arial", "verdana",
] as const;
export type AppearanceFont = (typeof APPEARANCE_FONTS)[number];

export type AppearanceBorderStyle = "solid" | "dashed" | "dotted";

export interface AppearanceStyle {
  borderColor: AppearanceColor;
  borderStyle: AppearanceBorderStyle;
  borderWidth: number;
  fillColor: AppearanceColor;
  textColor: AppearanceColor;
  font: AppearanceFont;
  fontSize: number;
}

export type TextAlignment = "left" | "center" | "right";
export type TextEditRole = "whiteout" | "replacement" | "redaction";

/** Swift `TextBoxDetails` (text boxes, typewriter text, white-outs, redaction marks). */
export interface TextBoxDetails {
  text: string;
  style: AppearanceStyle;
  isBold: boolean;
  alignment: TextAlignment;
  isItalic?: boolean;
  isTypewriter?: boolean;
  editRole?: TextEditRole;
  fillHex?: string;
  textHex?: string;
  fontPostScriptName?: string;
}

export interface SignaturePath { points: Vec2[] }

/** Swift `SignatureAppearance`. Visual only — no certificate. */
export interface SignatureAppearance {
  name: string;
  email: string;
  reason: string;
  location: string;
  includeTimestamp: boolean;
  timestamp: string;
  style: AppearanceStyle;
  logoData?: string;
  freehandPaths: SignaturePath[];
  inkOnly?: boolean;
  inkImageData?: string;
  inkAspectRatio?: number;
  inkCaption?: string;
  protectedSignatureID?: Id;
}

export type StampShape = "rectangle" | "roundedRectangle" | "doubleBorder" | "oval";
export type StampLogoPosition = "left" | "top" | "watermark";
export type StampDateFormat = "short" | "medium" | "long" | "dateTime";

/** Swift `CustomStampLayout`. */
export interface CustomStampLayout {
  shape: StampShape;
  logoData?: string;
  logoPosition: StampLogoPosition;
  showDate: boolean;
  dateFormat: StampDateFormat;
  showUser: boolean;
  userName: string;
  userPrefix: string;
  fillOpacity: number;
  templateID?: Id;
}

/** Swift `ApprovalStampDetails` — preset stamps, or custom when `custom` is set. */
export interface ApprovalStampDetails {
  title: string;
  name: string;
  date: string;
  project: string;
  style: AppearanceStyle;
  custom?: CustomStampLayout;
}

export interface HatchStyle { fillColor: AppearanceColor; hatchColor: AppearanceColor; hatchSpacing: number; fillOpacity: number }
export interface HighlightStyle { color: AppearanceColor; opacity: number }
export interface CloudStyle { color: AppearanceColor; lineWidth: number; arcRadius: number; fillColor: AppearanceColor; fillOpacity: number }

/** Swift `ToolChestImageAsset`. The pixels live in the user's library, not the document. */
export interface ToolChestImageAsset { id: Id; fileName: string; originalName: string; pixelWidth: number; pixelHeight: number; createdAt: string }

export type ShapeKind = "rectangle" | "square" | "circle" | "ellipse" | "triangle" | "diamond";
export type ShapePurpose =
  | "general" | "workZone" | "sidewalkClosure" | "pedDetourRoute" | "temporaryWalkway" | "crossingLocation"
  | "curbRampLocation" | "hazardArea" | "stagingArea" | "signLocation" | "utilityConflict" | "noWorkZone";
export type ShapeLabelPlacement = "inside" | "above" | "below" | "hidden";

/** Swift `ShapeMarkupStyle` (labeled shapes). */
export interface ShapeMarkupStyle {
  shape: ShapeKind;
  purpose: ShapePurpose;
  label: string;
  strokeColor: AppearanceColor;
  fillColor: AppearanceColor;
  fillOpacity: number;
  lineWidth: number;
  dashed: boolean;
  labelPlacement: ShapeLabelPlacement;
  fontSize: number;
  textColor: AppearanceColor;
  labelBackground: boolean;
  phase: string;
  notes: string;
}

export type QuickLengthUnitRaw = "ft" | "in" | "ft-in" | "yd" | "m" | "cm" | "mm";
export type QuickAreaUnitRaw = "SF" | "SY" | "SM" | "AC";

/** Swift `QuickMeasureStyle` (Measure Distance / Measure Area — markups, never takeoff). */
export interface QuickMeasureStyle {
  closed: boolean;
  lengthUnit: QuickLengthUnitRaw;
  areaUnit: QuickAreaUnitRaw;
  color: AppearanceColor;
  labelRotationDegrees: number;
  fontSize: number;
  /**
   * Cloud extension (the Mac's decoder ignores unknown keys): when set, this is a plain
   * straight Line markup — solid stroke of this width in page points, with its length shown
   * only when the sheet has a scale. Without it the markup is a dashed Measure Distance/Area.
   */
  lineWidth?: number;
}

/** Swift `AnchoredMarkupKind` case → associated value. */
export interface MarkupStyles {
  note: TextBoxDetails;
  signature: SignatureAppearance;
  /** `MOTSymbol` raw value, e.g. "roadWorkAhead". */
  mot: string;
  workArea: HatchStyle;
  approvalStamp: ApprovalStampDetails;
  image: ToolChestImageAsset;
  highlight: HighlightStyle;
  cloud: CloudStyle;
  shape: ShapeMarkupStyle;
  quickMeasure: QuickMeasureStyle;
}
export type MarkupKind = keyof MarkupStyles;
export const MARKUP_KINDS: readonly MarkupKind[] = ["note", "signature", "mot", "workArea", "approvalStamp", "image", "highlight", "cloud", "shape", "quickMeasure"];

interface MarkupCommon {
  id: Id;
  pageId: Id;
  /** Unrotated box in page space. Rotation turns about its centre. */
  bounds: Box;
  rotationDegrees: number;
  layer: string;
  /** Work areas, clouds and quick measures: polygon in the markup's unrotated page space. */
  vertices?: Vec2[];
  createdAt: string;
}

/** Swift `AnchoredMarkup`: `kind` is the enum case, `style` its associated value. */
export type Markup = { [K in MarkupKind]: MarkupCommon & { kind: K; style: MarkupStyles[K] } }[MarkupKind];
export type MarkupOf<K extends MarkupKind> = Extract<Markup, { kind: K }>;

export type MeasurementKind = "linear" | "arrow" | "area" | "count";
export type LinearUnitRaw = "LF" | "FT" | "M" | "GM";
export type AreaUnitRaw = "SF" | "SY" | "AC";

export interface MeasurementLabelStyle {
  rotationDegrees: number;
  backgroundColor: AppearanceColor;
  textColor: AppearanceColor;
  showBackground: boolean;
  fontSize?: number;
  offset?: Vec2;
  isHidden?: boolean;
}

export interface DepthSpec {
  value: number;
  unit: "in" | "ft";
  output: "CY" | "CF" | "TN";
  poundsPerCubicFoot?: number;
  tonsPerCubicYard?: number;
}

export interface PayItem { number: string; name: string; unit: string }

/** Swift `Measurement` (a takeoff row). */
export interface Measurement {
  id: Id;
  kind: MeasurementKind;
  pageId: Id;
  points: Vec2[];
  label: string;
  layer: string;
  createdAt: string;
  areaUnit: AreaUnitRaw;
  linearUnit: LinearUnitRaw;
  manualQuantity?: number;
  manualUnit?: string;
  sourceTag?: string;
  notes?: string;
  payItem?: PayItem;
  labelStyle?: MeasurementLabelStyle;
  cutouts?: Vec2[][];
  depth?: DepthSpec;
  color?: AppearanceColor;
}

/** A saved custom stamp design (Swift `CustomStamp`), kept with the document as data. */
export interface StampDefinition {
  id: Id;
  name: string;
  details: ApprovalStampDetails;
  /** Placed size in page points `[width, height]`. */
  size: [number, number];
  createdAt: string;
  updatedAt: string;
}

/** Fields `patchMarkup` may set. `vertices: null` removes them. */
export interface MarkupPatch {
  bounds?: Box;
  rotationDegrees?: number;
  layer?: string;
  pageId?: Id;
  vertices?: Vec2[] | null;
  style?: MarkupStyles[MarkupKind];
}
export const MARKUP_PATCH_FIELDS = ["bounds", "rotationDegrees", "layer", "pageId", "vertices", "style"] as const;

/** Fields `patchMeasurement` may set. `null` clears an optional field. */
export type MeasurementPatch = {
  [K in Exclude<keyof Measurement, "id" | "kind" | "createdAt">]?: undefined extends Measurement[K] ? Measurement[K] | null : Measurement[K];
};
export const MEASUREMENT_PATCH_FIELDS = [
  "pageId", "points", "label", "layer", "areaUnit", "linearUnit", "manualQuantity", "manualUnit", "sourceTag",
  "notes", "payItem", "labelStyle", "cutouts", "depth", "color",
] as const;
export const MEASUREMENT_OPTIONAL_FIELDS = ["manualQuantity", "manualUnit", "sourceTag", "notes", "payItem", "labelStyle", "cutouts", "depth", "color"] as const;
